/** @jsxImportSource ./simboluri-jsx */
// VEDEREA 3D A GRINZII DE FUNDARE.
//
// Axul de fundație era singurul element fără vedere proprie: pagina lui arăta
// secțiunea cotată și clădirea din jur, dar nu PIESA. Cauza era că până acum
// n-avea ce arăta — armătura era o singură carcasă constantă pe tot axul, iar
// un desen din care lipsesc tăierile de bare e un desen care minte. De când
// Pasul 18 taie barele pe înfășurătoare, piesa are ce arăta:
//
//   * secțiunea în T (talpă + inimă), beton transparent, la scară;
//   * carcasa CONTINUĂ sus și jos, pe toată lungimea;
//   * barele SUPLIMENTARE, fiecare pe intervalul ei — partea plină e cât
//     lucrează, cea subțire de la capete e decalarea a_l + ancorarea l_bd;
//   * etrierii CHIAR la pasul lor, zonă cu zonă (se vede unde se îndesesc);
//   * plasa transversală și repartiția din talpă;
//   * stâlpii de deasupra, schițați, ca să se vadă de ce momentul schimbă
//     semnul acolo unde-l schimbă.
//
// Aceleași convenții cu `Grinda3D` (Pasul 13): scenă Z-up, axa piesei pe X,
// fața de sus a secțiunii la z = 0, randare LA CERERE și `camera.up` setat
// ÎNAINTE de OrbitControls (altfel controlul orbitează în jurul lui Y global).

import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { incadreaza } from "./incadrare3d";
import { AxArmat } from "./armareAx";
import { PAL3D } from "./paletaDesen";

// Culorile vin din paleta unică a desenelor (`paletaDesen.ts`), varianta
// numerică pentru three.js — altfel aceeași bară ar fi altă culoare în 3D
// decât în planșă.
const COL_CONTINUE = PAL3D.barJos; // carcasa care merge cap la cap
const COL_SUPL = PAL3D.barSupl; // barele care se taie
const COL_ETRIER = PAL3D.etrieri;
const COL_TALPA = PAL3D.dirY; // plasa tălpii — direcția transversală
const COL_STALP = PAL3D.mut;

export default function AxFundare3D({
  ax,
  height = 380,
}: {
  ax: AxArmat;
  height?: number;
}) {
  const mount = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = mount.current;
    if (!el) return;
    const W = el.clientWidth || 900;
    const H = height;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(PAL3D.hartie);
    const camera = new THREE.PerspectiveCamera(40, W / H, 0.01, 200);
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
    });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    el.appendChild(renderer.domElement);

    // tot ce se alocă pe GPU și nu e prins de renderer.dispose()
    const gunoi: { dispose(): void }[] = [];

    const L = Math.max(Number(ax.L_m) || 1, 0.5);
    const B = Math.max(Number(ax.B_m) || 1, 0.2);
    const Hc = Math.max(Number(ax.H_c_m) || 0.8, 0.2);
    const ht = Math.min(Math.max(Number(ax.h_talpa_m) || Hc / 3, 0.05), Hc);
    const bw = Math.min(Math.max(Number(ax.b_inima_m) || B, 0.15), B);
    const c = Math.max((Number(ax.acoperire_mm) || 50) / 1000, 0.02);

    const beton = (
      lung: number,
      lat: number,
      inalt: number,
      xc: number,
      zc: number,
      op = 0.22
    ) => {
      const geo = new THREE.BoxGeometry(lung, lat, inalt);
      gunoi.push(geo);
      const mat = new THREE.MeshStandardMaterial({
        color: PAL3D.fundatie,
        transparent: true,
        opacity: op,
        depthWrite: false,
      });
      gunoi.push(mat);
      const m = new THREE.Mesh(geo, mat);
      m.position.set(xc, 0, zc);
      scene.add(m);
      const gm = new THREE.EdgesGeometry(geo);
      gunoi.push(gm);
      const lm = new THREE.LineBasicMaterial({
        color: PAL3D.fundatieLinie,
      });
      gunoi.push(lm);
      const muchii = new THREE.LineSegments(gm, lm);
      muchii.position.copy(m.position);
      scene.add(muchii);
    };

    // secțiunea în T: talpa jos (B lat), inima deasupra (b_w lat)
    const hi = Math.max(Hc - ht, 0.01);
    beton(L, B, ht, L / 2, -Hc + ht / 2, 0.3);
    beton(L, bw, hi, L / 2, -hi / 2, 0.18);

    // stâlpii de deasupra — schițați, ca reperele momentului
    for (const s of ax.reazeme_m ?? []) {
      const geo = new THREE.BoxGeometry(
        Math.min(0.45, L / 8),
        Math.min(0.45, bw),
        0.7
      );
      gunoi.push(geo);
      const mat = new THREE.MeshStandardMaterial({
        color: COL_STALP,
        transparent: true,
        opacity: 0.4,
        depthWrite: false,
      });
      gunoi.push(mat);
      const m = new THREE.Mesh(geo, mat);
      m.position.set(Math.min(Math.max(s, 0), L), 0, 0.35);
      scene.add(m);
    }

    // --- barele longitudinale (cilindri) -----------------------------------
    const bara = (
      x1: number,
      x2: number,
      y: number,
      z: number,
      phi: number,
      col: number,
      opacitate = 1
    ) => {
      const len = Math.max(x2 - x1, 0.02);
      const raza = Math.max(phi / 2000, 0.008);
      const geo = new THREE.CylinderGeometry(raza, raza, len, 8);
      gunoi.push(geo);
      const mat = new THREE.MeshStandardMaterial({
        color: col,
        transparent: opacitate < 1,
        opacity: opacitate,
      });
      gunoi.push(mat);
      const m = new THREE.Mesh(geo, mat);
      m.rotation.z = Math.PI / 2;
      m.position.set((x1 + x2) / 2, y, z);
      scene.add(m);
    };
    const raspandeste = (n: number, latime: number) => {
      const u = Math.max(latime, 0.05);
      return Array.from({ length: Math.max(n, 1) }, (_, i) =>
        n <= 1 ? 0 : -u / 2 + (u * i) / (n - 1)
      );
    };

    const util = bw - 2 * c;
    const zSus = -(c + ax.armare.sus.phi_mm / 2000);
    const zJos = -Hc + c + ax.armare.jos.phi_mm / 2000;
    for (const y of raspandeste(ax.armare.sus.n, util))
      bara(0, L, y, zSus, ax.armare.sus.phi_mm, COL_CONTINUE);
    for (const y of raspandeste(ax.armare.jos.n, util))
      bara(0, L, y, zJos, ax.armare.jos.phi_mm, COL_CONTINUE);

    // barele suplimentare: al doilea strat, spre interiorul secțiunii
    const supl = ax.armare.suplimentare ?? { jos: [], sus: [] };
    for (const fata of ["sus", "jos"] as const) {
      for (const z of (fata === "sus" ? supl.sus : supl.jos) ?? []) {
        const dz = 0.045 * (fata === "sus" ? -1 : 1);
        const zb = (fata === "sus" ? zSus : zJos) - dz;
        for (const y of raspandeste(z.n, util)) {
          // porțiunea în care bara chiar lucrează
          bara(z.s1_teoretic_m, z.s2_teoretic_m, y, zb, z.phi_mm, COL_SUPL);
          // prelungirile (decalare + ancorare), estompate
          bara(z.s1_m, z.s1_teoretic_m, y, zb, z.phi_mm, COL_SUPL, 0.35);
          bara(z.s2_teoretic_m, z.s2_m, y, zb, z.phi_mm, COL_SUPL, 0.35);
        }
      }
    }

    // --- etrierii, chiar la pasul zonei ------------------------------------
    const zoneEt = ax.armare.etrieri.zone ?? [
      {
        s1_m: 0,
        s2_m: L,
        L_m: L,
        pas_mm: ax.armare.etrieri.pas_mm,
        phi_mm: ax.armare.etrieri.phi_mm,
      },
    ];
    const pct: number[] = [];
    const y1 = -(bw / 2 - c);
    const y2 = bw / 2 - c;
    const zt = -c;
    const zb2 = -Hc + c;
    let nEt = 0;
    for (const z of zoneEt) {
      const pas = Math.max((Number(z.pas_mm) || 200) / 1000, 0.05);
      const n = Math.floor(Math.max(Number(z.L_m) || 0, 0) / pas);
      // plafon de desen: peste ~600 de etrieri se rărește, altfel un ax de
      // 30 m cu pas de 75 mm face 400 de dreptunghiuri fără să se vadă nimic
      const rar = Math.max(1, Math.ceil((n + 1) / 300));
      for (let i = 0; i <= n; i += rar) {
        const xe = Math.min(Number(z.s1_m) + i * pas, L);
        nEt++;
        pct.push(
          xe, y1, zt, xe, y2, zt,
          xe, y2, zt, xe, y2, zb2,
          xe, y2, zb2, xe, y1, zb2,
          xe, y1, zb2, xe, y1, zt
        );
      }
    }
    if (nEt) {
      const geoE = new THREE.BufferGeometry();
      geoE.setAttribute("position", new THREE.Float32BufferAttribute(pct, 3));
      gunoi.push(geoE);
      // etrierii sunt MULȚI: la opacitate plină carcasa lor ascunde exact
      // barele longitudinale pentru care există desenul (măsurat pe axul de
      // 18 m)
      const matE = new THREE.LineBasicMaterial({
        color: COL_ETRIER,
        transparent: true,
        opacity: 0.45,
      });
      gunoi.push(matE);
      scene.add(new THREE.LineSegments(geoE, matE));
    }

    // --- plasa tălpii: transversalele + repartiția --------------------------
    const plasa = ax.armare.plasa_talpa;
    if (plasa?.pas_mm && B > bw + 0.01) {
      const pas = Math.max(plasa.pas_mm / 1000, 0.05);
      const n = Math.floor(L / pas);
      const rar = Math.max(1, Math.ceil((n + 1) / 260));
      const p: number[] = [];
      const zP = -Hc + c;
      for (let i = 0; i <= n; i += rar) {
        const xe = Math.min(i * pas, L);
        p.push(xe, -B / 2 + c, zP, xe, B / 2 - c, zP);
      }
      const rep = ax.armare.repartitie_talpa;
      for (const y of raspandeste(rep?.n ?? 4, B - 2 * c))
        p.push(0, y, zP + 0.02, L, y, zP + 0.02);
      const geoP = new THREE.BufferGeometry();
      geoP.setAttribute("position", new THREE.Float32BufferAttribute(p, 3));
      gunoi.push(geoP);
      const matP = new THREE.LineBasicMaterial({
        color: COL_TALPA,
        transparent: true,
        opacity: 0.6,
      });
      gunoi.push(matP);
      scene.add(new THREE.LineSegments(geoP, matP));
    }

    // --- armăturile laterale ale inimii înalte -----------------------------
    const lat = ax.armare.laterale;
    if (lat?.n) {
      const pas = Math.max((lat.pas_mm || 300) / 1000, 0.1);
      const nPeFata = Math.max(Math.round(lat.n / 2), 1);
      for (let i = 1; i <= nPeFata; i++) {
        const z = -c - i * pas;
        if (z < -Hc + ht) break;
        for (const y of [y1, y2]) bara(0, L, y, z, lat.phi_mm, COL_CONTINUE);
      }
    }

    scene.add(new THREE.AmbientLight(0xffffff, 0.95));
    const dl = new THREE.DirectionalLight(0xffffff, 0.65);
    dl.position.set(2, -3, 4);
    scene.add(dl);

    // `camera.up` ÎNAINTE de OrbitControls — v. comentariul din `Grinda3D`
    camera.up.set(0, 0, 1);
    camera.position.set(L * 0.2, -Math.max(2.5, L * 0.8), Hc * 1.6);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(L / 2, 0, -Hc / 2);
    incadreaza(camera, controls, scene);

    // randare LA CERERE (scena e statică)
    const render = () => renderer.render(scene, camera);
    controls.addEventListener("change", render);
    render();
    return () => {
      controls.removeEventListener("change", render);
      controls.dispose();
      gunoi.forEach((x) => x.dispose());
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, [ax, height]);

  return <div ref={mount} style={{ width: "100%", height }} />;
}
